import {
  addGlobalContexts,
  clearGlobalContexts,
  newTracker,
} from '@snowplow/browser-tracker'
import { WebVitalsPlugin } from '@snowplow/browser-plugin-web-vitals'
import { PerformanceTimingPlugin } from '@snowplow/browser-plugin-performance-timing'
import * as webVitals from 'web-vitals'

import config from 'constants/config'
import Snowplow, { getSnowplowCookieLifetime } from 'constants/snowplow'
import { isConsentRequiredOnClient } from '../consent/clientScope'
import { APP_CONTEXT_SCHEMA } from 'constants/analytics'

import { baseContext } from './snowplow/events'
import { consentContext } from './snowplow/eventContexts/consentContext'
import overrideTrackerIds from './snowplow/helpers/overrideTrackedIds'
import { resolveTrackingHost } from './resolveTrackingHost'

import { reportClientError } from 'services/errorReportingService'

import { getWhiteLabelAppConfig } from 'lib/whitelabels/whitelabels'
import { getCookies, deleteCookie } from 'lib/web/cookieUtils'

export { trackClientEvent, trackEvent } from './trackClientEvent'

if (typeof window !== 'undefined') {
  (window as any).webVitals = webVitals
}

/**
 * True when the page is running from a prebuilt static bundle rather than a server-rendered
 * response. `constants/config` falls back to `__STATIC_CONFIG__` only when the server did not
 * serialize a `__PUBLIC_CONFIG__`, so the absence of the latter is what distinguishes the two.
 */
function isStaticConfigBundle(): boolean {
  if (typeof window === 'undefined') return false
  const globalScope = window as any
  return !globalScope.__PUBLIC_CONFIG__ && !!globalScope.__STATIC_CONFIG__
}

let listenerAdded = false
export function init() {
  if (!config.TRACKING_HOST) return

  // Backstop for the one path that bypasses `createConfig`: a pre-built static bundle whose
  // `__STATIC_CONFIG__` was baked in production but is being served from a developer's machine.
  const trackingHost = resolveTrackingHost({
    trackingHost: config.TRACKING_HOST,
    appEnv: config.APP_ENV,
    // Only consulted on the static-bundle path. On the SSR path the server already resolved this
    // from CUSTOMER_PORTAL_HOST, which Pulumi sets and is authoritative; re-deciding here from
    // window.location.hostname is weaker evidence that could only turn a correct answer into a
    // wrong one — a mobile webview served from a local origin would lose real production analytics.
    servingHost: isStaticConfigBundle() ? window.location.hostname : undefined,
    warn: message => {
      // A console warning inside a production webview reaches nobody. If this fires in production
      // it means real analytics are being dropped, so surface it where we would actually see it.
      if (config.APP_ENV === 'production') reportClientError(new Error(message))
      console.warn(message)
    },
  })

  const mobileAppPlatform = getWhiteLabelAppConfig().platform

  const tracker = newTracker(Snowplow.trackerName, trackingHost, {
    appId: mobileAppPlatform ? config.wlMobileApp?.[mobileAppPlatform]?.snowplow?.appId : config.snowplow.appId,
    platform: mobileAppPlatform ? 'app' : 'web',
    cookieLifetime: getSnowplowCookieLifetime(isConsentRequiredOnClient()),
    // Snowplow v4 defaults to root-domain cookie discovery; keep the existing per-host cookie behavior.
    discoverRootDomain: false,
    // Snowplow v4 defers cookie writes by default. Keep init cookie writes synchronous so
    // overrideTrackerIds can safely read-modify-write the tracker cookies immediately after init.
    synchronousCookieWrite: true,
    plugins: [
      WebVitalsPlugin({
        loadWebVitalsScript: false,
      }),
      PerformanceTimingPlugin(),
    ],
    // you must specify the path since the default path
    // /com.snowplowanalytics.snowplow/tp2 will be blocked by adblock
    postPath: Snowplow.postPath,
    // not-encoding the data makes debugging easier
    encodeBase64: false,
  })

  try {
    // If user came from mobile webview or we want to override the userId data, we grab custom cookies to
    // override Snowplow with those Ids
    const dict = getCookies()
    if (
      tracker &&
      (dict[Snowplow.domainUserIdCookie] || dict[Snowplow.sessionIdCookie] || dict[Snowplow.sessionIdxCookie])
    ) {
      const didOverrideTrackerIds = overrideTrackerIds(
        tracker,
        dict[Snowplow.sessionIdCookie],
        dict[Snowplow.domainUserIdCookie],
        Number.isFinite(Number(dict[Snowplow.sessionIdxCookie])) ? Number(dict[Snowplow.sessionIdxCookie]) : undefined,
      )

      if (didOverrideTrackerIds) {
        deleteCookie(Snowplow.sessionIdxCookie, '/')
        deleteCookie(Snowplow.sessionIdCookie, '/')
      }
    }
  } catch(err) {
    reportClientError(new Error('Snowplow init error'), { error: err.message })
  }

  if (!listenerAdded) {
    listenerAdded = true
    resetContext()
  }
}

export function resetContext() {
  clearGlobalContexts()

  addGlobalContexts([
    {
      schema: APP_CONTEXT_SCHEMA,
      data: {
        appVersion: config.COMMIT_SHA || '-',
      },
    },
    baseContext,
    consentContext,
  ])
}
